import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import {
  getAllProfiles,
  createProfile,
  updateProfile as updateProfileAPI,
  deleteProfile as deleteProfileAPI,
  createGroup,
  updateGroup as updateGroupAPI,
  deleteGroup as deleteGroupAPI,
  CalculatorProfile,
  CalculatorProfileGroup,
  CreateProfileRequest,
  UpdateProfileRequest,
  CreateGroupRequest,
  UpdateGroupRequest,
} from '../services/profileService';

// Legacy Applicant interface for backward compatibility
export interface Applicant {
  id: string;
  name: string;
  age: string;
  monthlyIncome: string;
  otherAnnualIncome: string;
  carLoan: string;
  housingLoan: string;
  creditCard: string;
  otherLoan: string;
}

// Legacy UserProfile interface for backward compatibility
export interface UserProfile {
  id: string;
  name: string;
  applicants: Applicant[];
  createdAt: string;
  updatedAt: string;
}

export type SelectedProfileType = 'profile' | 'group' | null;

interface SelectedProfile {
  type: 'profile';
  id: number;
  profile: CalculatorProfile;
}

interface SelectedGroup {
  type: 'group';
  id: number;
  group: CalculatorProfileGroup;
}

type SelectedProfileData = SelectedProfile | SelectedGroup | null;

interface ProfileContextType {
  // Individual profiles
  profiles: CalculatorProfile[];
  // Groups
  groups: CalculatorProfileGroup[];
  // Selected profile/group
  selectedProfile: SelectedProfileData;
  selectedProfileId: string | null; // Legacy: format "profile-{id}" or "group-{id}"
  
  // Loading state
  isLoading: boolean;
  refreshProfiles: () => Promise<void>;
  
  // Individual Profile operations
  addProfile: (data: CreateProfileRequest) => Promise<CalculatorProfile>;
  updateProfile: (id: number, data: UpdateProfileRequest) => Promise<CalculatorProfile>;
  deleteProfile: (id: number) => Promise<void>;
  
  // Group operations
  addGroup: (data: CreateGroupRequest) => Promise<CalculatorProfileGroup>;
  updateGroup: (id: number, data: UpdateGroupRequest) => Promise<CalculatorProfileGroup>;
  deleteGroup: (id: number) => Promise<void>;
  
  // Selection operations
  selectProfile: (id: number | null) => void;
  selectGroup: (id: number | null) => void;
  clearSelection: () => void;
  
  // Legacy compatibility methods
  getSelectedProfile: () => UserProfile | null;
  getFirstApplicant: () => Applicant | null;
  
  // Get selected profile/group name for display
  getSelectedName: () => string | null;
  
  // Get all profiles from selected group (for calculators)
  getSelectedProfiles: () => CalculatorProfile[];
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

const SELECTED_PROFILE_KEY = 'sc_selectedProfileId';

// Convert CalculatorProfile to legacy UserProfile format
const profileToUserProfile = (profile: CalculatorProfile): UserProfile => {
  const currentYear = new Date().getFullYear();
  const birthYear = profile.BirthYear || null;
  const age = birthYear ? String(currentYear - birthYear) : '';
  
  return {
    id: `profile-${profile.Id}`,
    name: profile.Name,
    applicants: [
      {
        id: `applicant-${profile.Id}`,
        name: profile.Name,
        age: age,
        monthlyIncome: profile.MonthlyIncome ? String(profile.MonthlyIncome) : '',
        otherAnnualIncome: profile.OtherAnnualIncome ? String(profile.OtherAnnualIncome) : '',
        carLoan: profile.CarLoan != null ? String(profile.CarLoan) : '',
        housingLoan: profile.HousingLoan != null ? String(profile.HousingLoan) : '',
        creditCard: profile.CreditCard != null ? String(profile.CreditCard) : '',
        otherLoan: profile.OtherLoan != null ? String(profile.OtherLoan) : '',
      },
    ],
    createdAt: profile.CreatedAt || new Date().toISOString(),
    updatedAt: profile.UpdatedAt || new Date().toISOString(),
  };
};

// Convert CalculatorProfileGroup to legacy UserProfile format (using first profile)
const groupToUserProfile = (group: CalculatorProfileGroup): UserProfile | null => {
  if (!group.profiles || group.profiles.length === 0) {
    return null;
  }
  
  // Use first profile in group
  const firstProfile = group.profiles[0];
  return profileToUserProfile(firstProfile);
};

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [profiles, setProfiles] = useState<CalculatorProfile[]>([]);
  const [groups, setGroups] = useState<CalculatorProfileGroup[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<SelectedProfileData>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mirrors of the two lists, kept up to date the moment a profile/group is created.
  // The select handlers read these: selecting a just-created profile in the same tick
  // would otherwise miss it, since the state update has not rendered yet.
  const profilesRef = useRef<CalculatorProfile[]>([]);
  const groupsRef = useRef<CalculatorProfileGroup[]>([]);
  useEffect(() => { profilesRef.current = profiles; }, [profiles]);
  useEffect(() => { groupsRef.current = groups; }, [groups]);

  // Load selected profile from localStorage on mount
  useEffect(() => {
    try {
      const storedSelectedId = localStorage.getItem(SELECTED_PROFILE_KEY);
      if (storedSelectedId) {
        setSelectedProfileId(storedSelectedId);
      }
    } catch (error) {
      console.error('Error loading selected profile:', error);
    }
  }, []);

  // Load profiles from API
  const refreshProfiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllProfiles();
      setProfiles(data.profiles);
      setGroups(data.groups);
      
      // Restore selection if exists (use current selectedProfileId from state)
      const currentSelectedId = selectedProfileId;
      if (currentSelectedId) {
        if (currentSelectedId.startsWith('profile-')) {
          const id = parseInt(currentSelectedId.replace('profile-', ''));
          const profile = data.profiles.find(p => p.Id === id);
          if (profile) {
            setSelectedProfile({ type: 'profile', id: profile.Id, profile });
          } else {
            setSelectedProfile(null);
            setSelectedProfileId(null);
            localStorage.removeItem(SELECTED_PROFILE_KEY);
          }
        } else if (currentSelectedId.startsWith('group-')) {
          const id = parseInt(currentSelectedId.replace('group-', ''));
          const group = data.groups.find(g => g.Id === id);
          if (group) {
            setSelectedProfile({ type: 'group', id: group.Id, group });
          } else {
            setSelectedProfile(null);
            setSelectedProfileId(null);
            localStorage.removeItem(SELECTED_PROFILE_KEY);
          }
        }
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProfileId]);

  // Load profiles on mount
  useEffect(() => {
    refreshProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save selected profile ID to localStorage
  useEffect(() => {
    if (selectedProfileId) {
      localStorage.setItem(SELECTED_PROFILE_KEY, selectedProfileId);
    } else {
      localStorage.removeItem(SELECTED_PROFILE_KEY);
    }
  }, [selectedProfileId]);

  // Individual Profile operations
  const addProfileHandler = async (data: CreateProfileRequest): Promise<CalculatorProfile> => {
    const newProfile = await createProfile(data);
    profilesRef.current = [newProfile, ...profilesRef.current];
    setProfiles((prev) => [newProfile, ...prev]);
    return newProfile;
  };

  const updateProfileHandler = async (id: number, data: UpdateProfileRequest): Promise<CalculatorProfile> => {
    const updated = await updateProfileAPI(id, data);
    setProfiles((prev) => prev.map((p) => (p.Id === id ? updated : p)));

    // Refresh the copies embedded in groups — group cards, group selection and the
    // calculators' fund/borrower summing all read these, so a stale copy would keep
    // showing the profile's old values (e.g. an outdated CPF) after an edit.
    setGroups((prev) =>
      prev.map((group) =>
        group.profiles?.some((p) => p.Id === id)
          ? { ...group, profiles: group.profiles.map((p) => (p.Id === id ? updated : p)) }
          : group
      )
    );

    // Refresh the selection from the latest state, not this render's closure: a
    // calculator save-back updates each group member in turn, and a stale closure
    // would rebuild the group from pre-save members — reverting the first member's
    // just-saved fields (citizenship, FTA remission, …) in the selected copy.
    setSelectedProfile((prev) => {
      // Update selected profile if it's the one being updated
      if (prev?.type === 'profile' && prev.id === id) {
        return { type: 'profile', id: updated.Id, profile: updated };
      }
      // Update a selected group containing the edited profile, so loaded calculators
      // re-derive from the fresh member data (mirrors the individual-selection case).
      if (prev?.type === 'group' && prev.group.profiles?.some((p) => p.Id === id)) {
        return {
          type: 'group',
          id: prev.id,
          group: {
            ...prev.group,
            profiles: prev.group.profiles.map((p) => (p.Id === id ? updated : p)),
          },
        };
      }
      return prev;
    });

    return updated;
  };

  const deleteProfileHandler = async (id: number): Promise<void> => {
    await deleteProfileAPI(id);
    setProfiles((prev) => prev.filter((p) => p.Id !== id));
    
    // Clear selection if deleted profile was selected
    if (selectedProfile?.type === 'profile' && selectedProfile.id === id) {
      setSelectedProfile(null);
      setSelectedProfileId(null);
    }
    
    // Remove from groups
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        profiles: group.profiles?.filter((p) => p.Id !== id) || [],
      }))
    );
  };

  // Group operations
  const addGroupHandler = async (data: CreateGroupRequest): Promise<CalculatorProfileGroup> => {
    const newGroup = await createGroup(data);
    groupsRef.current = [newGroup, ...groupsRef.current];
    setGroups((prev) => [newGroup, ...prev]);
    return newGroup;
  };

  const updateGroupHandler = async (id: number, data: UpdateGroupRequest): Promise<CalculatorProfileGroup> => {
    const updated = await updateGroupAPI(id, data);
    setGroups((prev) => prev.map((g) => (g.Id === id ? updated : g)));
    
    // Update selected group if it's the one being updated
    if (selectedProfile?.type === 'group' && selectedProfile.id === id) {
      setSelectedProfile({ type: 'group', id: updated.Id, group: updated });
    }
    
    return updated;
  };

  const deleteGroupHandler = async (id: number): Promise<void> => {
    await deleteGroupAPI(id);
    setGroups((prev) => prev.filter((g) => g.Id !== id));
    
    // Clear selection if deleted group was selected
    if (selectedProfile?.type === 'group' && selectedProfile.id === id) {
      setSelectedProfile(null);
      setSelectedProfileId(null);
    }
  };

  // Selection operations
  const selectProfileHandler = (id: number | null) => {
    if (id === null) {
      setSelectedProfile(null);
      setSelectedProfileId(null);
      return;
    }
    
    const profile = profilesRef.current.find((p) => p.Id === id) ?? profiles.find((p) => p.Id === id);
    if (profile) {
      setSelectedProfile({ type: 'profile', id: profile.Id, profile });
      setSelectedProfileId(`profile-${profile.Id}`);
    }
  };

  const selectGroupHandler = (id: number | null) => {
    if (id === null) {
      setSelectedProfile(null);
      setSelectedProfileId(null);
      return;
    }
    
    const group = groupsRef.current.find((g) => g.Id === id) ?? groups.find((g) => g.Id === id);
    if (group) {
      setSelectedProfile({ type: 'group', id: group.Id, group });
      setSelectedProfileId(`group-${group.Id}`);
    }
  };

  const clearSelection = () => {
    setSelectedProfile(null);
    setSelectedProfileId(null);
  };

  // Legacy compatibility methods
  const getSelectedProfile = (): UserProfile | null => {
    if (!selectedProfile) return null;
    
    if (selectedProfile.type === 'profile') {
      return profileToUserProfile(selectedProfile.profile);
    } else {
      return groupToUserProfile(selectedProfile.group);
    }
  };

  const getFirstApplicant = (): Applicant | null => {
    const userProfile = getSelectedProfile();
    if (!userProfile || !userProfile.applicants || userProfile.applicants.length === 0) {
      return null;
    }
    return userProfile.applicants[0];
  };

  const getSelectedName = (): string | null => {
    if (!selectedProfile) return null;
    
    if (selectedProfile.type === 'profile') {
      return selectedProfile.profile.Name;
    } else {
      return selectedProfile.group.Name;
    }
  };

  const getSelectedProfiles = (): CalculatorProfile[] => {
    if (!selectedProfile) return [];
    
    if (selectedProfile.type === 'profile') {
      return [selectedProfile.profile];
    } else {
      return selectedProfile.group.profiles || [];
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        groups,
        selectedProfile,
        selectedProfileId,
        isLoading,
        refreshProfiles,
        addProfile: addProfileHandler,
        updateProfile: updateProfileHandler,
        deleteProfile: deleteProfileHandler,
        addGroup: addGroupHandler,
        updateGroup: updateGroupHandler,
        deleteGroup: deleteGroupHandler,
        selectProfile: selectProfileHandler,
        selectGroup: selectGroupHandler,
        clearSelection,
        getSelectedProfile,
        getFirstApplicant,
        getSelectedName,
        getSelectedProfiles,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};
